<script setup>
/**
 * Displays a scroll‑aware, paginated grid of product cards driven by two Pinia
 * stores (`productListStore` + `filterStore`). Handles:
 *   • Skeleton placeholders while data loads.
 *   • An empty‑result state when filters return zero products.
 *   • Client‑side scrolling when pagination links are clicked (via `scrollTop`).
 *   • Waiting for the Sylius back‑end flag (`syliusLoaded`) before proceeding
 *     with further UI updates to avoid race conditions.
 *   • Mixing category content cards (theme_*) into the product list at
 *     editor‑configured positions.
 */
import { computed, onMounted, watch } from 'vue'
import { useProductListStore } from '@/store'
import { useFilterStore } from '@/store/filter'
import Card from './ModuleCard.vue'
import Pagination from './ModulePagination.vue'
import { loadLanguageAsync } from '@/plugins/i18n'
import { gsap } from 'gsap'
import ThemeBillboard from '@/components/ThemeBillboard.vue'
import ThemeEmotionalCard from '@/components/ThemeEmotionalCard.vue'
import ThemeHeadlineParagraph from '@/components/ThemeHeadlineParagraph.vue'
import ThemeHero from '@/components/ThemeHero.vue'
import ThemeCard from '@/components/ThemeCard.vue'
import ThemeImage from '@/components/ThemeImage.vue'
import ThemeVideo from '@/components/ThemeVideo.vue'
import ThemeHtml from '@/components/ThemeHtml.vue'
import ThemePromoCard from '@/components/ThemePromoCard.vue'

// Initialize the stores
const store = useProductListStore()
const filterStore = useFilterStore()

/**
 * Signals when filter metadata and product list have finished loading. Used to
 * switch between skeleton and real content.
 */
const loaded = computed(() => filterStore.loaded && store.loaded)

/**
 * `true` when current filter combination yields zero products.
 */
const emptyFilterResult = computed(() => store.isEmptyFilterResult)

// current page is 1-based, store.pageNumber is 0-based
const currentPage = computed(() => {
  const raw = store.pageNumber ?? 0
  const n = Number(raw)
  // 0 -> first page, 1 -> second, ...
  return (Number.isFinite(n) ? n : 0) + 1
})

/**
 * Smooth‑scroll the viewport to the top of the product list, leaving a 100‑px
 * offset for sticky headers. Triggered by the Pagination component via emit.
 */
const scrollTop = () => {
  const el = document.querySelector('.product__list')
  const domRect = el.getBoundingClientRect()
  const scrollingElement = document.scrollingElement || document.documentElement
  gsap.to(window, { duration: 0.5, scrollTo: { y: domRect.top + scrollingElement.scrollTop - 100, autoKill: false } })
}

/**
 * Resolve after the Sylius back‑end flag flips to `true`, or after a timeout.
 * Prevents UI actions that depend on back‑end availability from firing too
 * early. Returns a promise that always resolves.
 */
function waitForSylius (timeoutMs = 4000) {
  return new Promise(resolve => {
    if (store.syliusLoaded) return resolve()
    const stop = watch(() => store.syliusLoaded, v => {
      if (v) { stop(); resolve() }
    })
    // safety timeout
    setTimeout(() => { stop(); resolve() }, timeoutMs)
  })
}

// helper: pick component by content_type
const componentForCard = (card) => {
  const type = (card?.content_type || '').toLowerCase()

  if (type === 'teaser') return ThemeBillboard
  if (type === 'emotionalcard') return ThemeEmotionalCard
  if (type === 'header') return ThemeHeadlineParagraph
  if (type === 'hero') return ThemeHero
  if (type === 'card') return ThemeCard
  if (type === 'image') return ThemeImage
  if (type === 'video') return ThemeVideo
  if (type === 'cmhtml') return ThemeHtml
  if (type === 'promocard') return ThemePromoCard

  // default
  return ThemeEmotionalCard
}

// helper: build CSS class modifiers from layout + content_type
const categoryCardClasses = (card) => {
  const layout = (card?.layout || '').toLowerCase()
  const out = {}
  if (layout) out[`card__item--layout-${layout}`] = true
  return out
}

/** categoryCards is already an ARRAY via the store getter */
const categoryCardsSorted = computed(() => {
  const current = currentPage.value

  return [...(store.categoryCards ?? [])]
    .map(c => {
      const position = Number(c?.position ?? 0)

      // normalize is_paginated:
      //  - null/undefined/''/0 => 0  (visible on all pages)
      //  - >0 => visible only on that page (1-based)
      let flag = c?.is_paginated
      if (flag === null || flag === undefined || flag === '') {
        flag = 0
      }
      flag = Number(flag)
      if (!Number.isFinite(flag)) {
        flag = 0
      }

      return {
        ...c,
        position,
        is_paginated: flag,
      }
    })
    // keep cards for all pages (0) or only for the current page
    .filter(c => c.is_paginated === 0 || c.is_paginated === current)
    .sort((a, b) => a.position - b.position)
})

// helper: returns 0/1/2 placeholders needed to reach next multiple of 3
const padsNeeded = (count) => (count % 3 === 0 ? 0 : 3 - (count % 3))

const renderStreamLoaded = computed(() => {
  const prods = store.items ?? []
  const cards = prods.length > 0 ? [...categoryCardsSorted.value] : []
  const out = []
  let i = 0

  for (let n = 1; n <= prods.length; n++) {
    // inject all cards scheduled at/ before product n
    while (i < cards.length && cards[i].position <= n) {
      const card = cards[i++]
      const layout = String(card.layout || '').toLowerCase()

      // if this category card is a "row" → pad BEFORE it
      if (layout === 'row') {
        const need = padsNeeded(out.length)
        for (let k = 0; k < need; k++) {
          out.push({ kind: 'ph', key: `ph-before-${card.uid}-${k}` })
        }
      }

      out.push({ kind: 'category', key: `cat-${card.uid ?? `${n}-${i}`}`, card })
    }

    // then the product n
    const p = prods[n - 1]
    out.push({ kind: 'product', key: `prod-${p?.itemNo ?? n}`, item: p })
  }

  // remaining category cards (position > products.length) → append
  while (i < cards.length) {
    const card = cards[i++]
    const layout = String(card.layout || '').toLowerCase()

    if (layout === 'row') {
      const need = padsNeeded(out.length)
      for (let k = 0; k < need; k++) {
        out.push({ kind: 'ph', key: `ph-tail-${card.uid}-${k}` })
      }
    }

    out.push({ kind: 'category', key: `cat-tail-${card.uid ?? i}`, card })
  }

  return out
})

// Kick off language loading + initial product fetch on mount.
onMounted(() => {
  loadLanguageAsync().then(() => {
    store.setPredefinedFilter()
    store.get()
  })
})

// Watch the `loaded` flag; once true, handle Sylius call and wait for it.
watch(
  loaded,
  async () => {
    if (!loaded.value) {
      return
    }

    setTimeout(async () => {
      store.handleSyliusCall()
      await waitForSylius()
    }, 300)
  },
  { immediate: true }
)
</script>

<template>
  <div class="list__list">
    <div class="product__list">
      <div class="list__list" v-if="loaded">
        <div v-if="emptyFilterResult" class="empty-result-container">
          <span>{{ $t('filterLabels.noResultsFound') || 'For the chosen filter options no matching products were found.' }}</span>
        </div>
        <template v-for="row in renderStreamLoaded" :key="row.key">
          <div v-if="row.kind === 'ph'" class="card__card--placeholder"></div>
          <component
            v-else-if="row.kind === 'category'"
            :is="componentForCard(row.card)"
            :card="row.card"
            class="card__item--category-card"
            :class="categoryCardClasses(row.card)"
          />
          <Card v-else :item="row.item"/>
        </template>
        <div class="card__card--placeholder"></div>
      </div>
      <div class="list__list" v-else>
        <template v-for="n in 9" :key="n">
          <div class="card__item card__item--loading">
            <div class="item__image"></div>
            <div class="item__content">
              <div class="content__headline"><span></span><span></span></div>
              <div class="content__meta"><span></span></div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <Pagination v-if="loaded && !emptyFilterResult && store.totalPages > 1" @scrollTop="scrollTop" />
  </div>
</template>